import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount } from "../app/session";
import { saveStartArea, type StartBereich } from "../app/startziel";
import { toast } from "./Toast";
import { t } from "../lib/sprache";

/**
 * Einstellung „Nach dem Login: Betrieb oder Agentur“ (0330), nur für Plattform-Admins. In der Betreiber-Konsole (Übersicht)
 * als kompakte Auswahl, im Profil als eigener Abschnitt. Standard ist der Betrieb.
 */
export function StartseiteWahl({ variante = "kompakt" }: { variante?: "kompakt" | "abschnitt" }) {
  const { account } = useAccount();
  const qc = useQueryClient();
  const id = useId();
  const [busy, setBusy] = useState(false);
  if (!account?.isPlatformAdmin) return null;
  const wert: StartBereich = account.startArea === "agentur" ? "agentur" : "betrieb";
  const keineAgentur = account.memberships.length === 0 && account.talentLinks.length === 0;

  async function waehlen(next: StartBereich) {
    if (next === wert || !account) return;
    setBusy(true);
    try {
      await saveStartArea(account.session.userId, next);
      await qc.invalidateQueries({ queryKey: ["account", account.session.userId] });
      toast(next === "betrieb" ? t("Nach dem Login öffnet sich die Betreiber-Konsole.", "After sign-in the operator console opens.")
        : t("Nach dem Login öffnet sich deine Agentur.", "After sign-in your agency opens."));
    } catch (e) {
      toast(e instanceof Error ? e.message : t("Das hat gerade nicht geklappt.", "That did not work just now."), "alert");
    } finally {
      setBusy(false);
    }
  }

  const auswahl = (
    <select id={`${id}-start`} value={wert} disabled={busy} aria-busy={busy} onChange={(e) => void waehlen(e.target.value as StartBereich)}>
      <option value="betrieb">{t("Betrieb", "Operations")}</option>
      <option value="agentur">{t("Agentur", "Agency")}</option>
    </select>
  );
  if (variante === "kompakt") {
    return <label htmlFor={`${id}-start`} className="row tk-startseite" style={{ gap: 6, alignItems: "center" }}>{t("Nach dem Login:", "After sign-in:")} {auswahl}</label>;
  }
  return (
    <section className="panel stack" aria-labelledby={`${id}-titel`}>
      <h3 id={`${id}-titel`}>{t("Startseite nach dem Login", "Start page after sign-in")}</h3>
      <label htmlFor={`${id}-start`}>{t("Nach dem Login öffnen", "Open after sign-in")}</label>
      {auswahl}
      <small>{keineAgentur ? t("Dieses Konto hat keine Agentur; es öffnet sich immer die Betreiber-Konsole.", "This account has no agency; the operator console always opens.")
        : t("Nur für den Plattform-Betrieb sichtbar. Die Betreiber-Konsole erreichst du außerdem über das Kontomenü oben rechts.", "Only visible to platform operators. The operator console is also in the account menu at the top right.")}</small>
    </section>
  );
}
